<template>
  <BaseCard
    class="h-max mt-5 w-4/5 pt-5 flex-col justify-between items-center"
    :no-padding="true"
  >
    <div
      class="flex w-full items-center justify-between px-5 border-b border-border-normal pb-5"
    >
      <div class="flex items-center">
        <img
          src="../../assets/icons/swipe-file.png"
          class="w-10 h-10"
        >
        <div class="ml-3">
          <BaseText
            type="label"
            size="md"
            class="text-neutral-900 font-medium"
          >
            Swipe File
          </BaseText>

          <BaseText
            type="body"
            size="sm"
            class="text-text-normal w-52 overflow-visible whitespace-nowrap"
          >
            You have saved {{ getUser?.counts?.ads }} Ads!
          </BaseText>
        </div>
      </div>
      <div class="flex gap-5 items-center">
        <BaseButton
          type="primary"
          size="sm"
          outlined
          class="w-max flex justify-between items-center gap-2 flex-nowrap ml-auto shadow-sm"
          @click="showAdvertisementModal = true"
        >
          <div class="flex gap-1 items-center -ml-2">
            <img
              src="../../assets/icons/plus-icon.svg"
              alt=""
              class="transform -rotate-90"
            >
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              Saved Ads
            </BaseText>
          </div>
        </BaseButton>
        <BaseButton
          type="primary"
          size="sm"
          outlined
          class="w-max flex justify-between items-center gap-2 flex-nowrap ml-auto shadow-sm"
          @click="showManageBoardModal = true"
        >
          <div class="flex gap-1.5 items-center -ml-1">
            <img
              src="../../assets/icons/create-board-icon.svg"
              alt=""
            >
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              Create Board
            </BaseText>
          </div>
        </BaseButton>
      </div>
    </div>

    <!-- Saving Extension Status -->

    <div class="flex flex-nowrap gap-0">
      <div class="flex flex-col w-1/2 h-max p-4 border-r border-border-normal">
        <BaseText
          type="body"
          size="sm"
          class="text-text-disabled pb-2 pl-2"
        >
          <div class="flex items-center gap-2">
            Saving Extension Status
            <BaseTooltip dark>
              <img
                slot="reference"
                class="cursor-pointer"
                src="../../assets/icons/questionmark.svg"
              >
              <div
                slot="content"
                class="px-2 w-36"
              >
                Health of Chrome Extension Saving
              </div>
            </BaseTooltip>
          </div>
        </BaseText>
        <div
          v-for="status in extensionStatuses"
          :key="status?.text"
        >
          <a
            :href="status?.link"
            target="_blank"
            rel="noopener noreferrer"
            class="cursor-pointer"
          >
            <div
              class="flex gap-2 items-center w-full mb-1 px-2 py-2 hover:bg-background-normal rounded-md cursor-pointer group transition-all duration-200 ease-linear"
            >
              <img
                v-if="status?.icon === 'facebook'"
                src="../../assets/images/alternate-facebook-icon.svg"
                alt=""
                class="w-6 h-6"
              >
              <img
                v-else-if="status?.icon === 'tik tok'"
                src="../../assets/images/alternate-tiktok-icon.svg"
                alt=""
                class="w-6 h-6"
              >
              <img
                v-else-if="status?.icon === 'linkedin'"
                src="../../assets/images/alternate-linkden-icon.svg"
                alt=""
                class="w-6 h-6"
              >
              <img
                v-else-if="status?.icon === 'google'"
                src="../../assets/images/google-logo.png"
                alt=""
                class="w-4 h-4 mx-1"
              >
              <img
                v-else-if="status?.icon === 'instagram'"
                src="../../assets/images/instagram-small.png"
                alt=""
                class="w-4 h-4 mx-1"
              >
              <BaseText
                type="body"
                size="sm"
                class="text-text-muted"
              >
                {{ status?.text }}
              </BaseText>
              <BaseBadge
                v-if="status?.status === 'Working'"
                green
              >
                <BaseText
                  type="body"
                  size="xs"
                >
                  {{ status?.status }}
                </BaseText>
              </BaseBadge>
              <BaseBadge
                v-if="status?.status === 'Under Maintenance'"
                yellow
              >
                <BaseText
                  type="body"
                  size="xs"
                >
                  {{ status?.status }}
                </BaseText>
              </BaseBadge>
              <BaseBadge
                v-if="status?.status === 'Coming Soon'"
                gray
              >
                <BaseText
                  type="body"
                  size="xs"
                  class="text-text-disabled"
                >
                  {{ status?.status }}
                </BaseText>
              </BaseBadge>
              <img
                src="../../assets/icons/external-tab.svg"
                alt=""
                class="w-5 h-5 ml-auto mr-2 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-linear"
              >
            </div>
          </a>
        </div>
      </div>

      <!-- Starred Boards Section -->

      <div
        v-if="!loading && getStarredBoards?.length"
        class="flex w-1/2 h-96 flex-col p-4 pb-6"
      >
        <BaseText
          type="body"
          size="sm"
          class="text-text-disabled pl-2 pb-2"
        >
          <div class="flex items-center gap-2">
            Starred Boards
            <BaseTooltip dark>
              <img
                slot="reference"
                class="cursor-pointer"
                src="../../assets/icons/questionmark.svg"
              >
              <div
                slot="content"
                class="px-2 w-36"
              >
                Star boards you use most
              </div>
            </BaseTooltip>
          </div>
        </BaseText>
        <div
          v-if="getStarredBoards.length && !loading"
          class="flex flex-col gap-2 flex-1 overflow-y-auto scrollbar-hide"
        >
          <div
            v-for="board in getStarredBoards"
            :key="board?.id"
            class="flex flex-nowrap items-center hover:bg-background-normal rounded-md group cursor-pointer"
          >
            <router-link
              :key="board?.id"
              :to="{
                name: 'BoardsDetailsView',
                params: { id: board?.id, board: board },
              }"
              class="flex gap-2 cursor-pointer transition-all flex-1 p-2 pr-0"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.03502 16.6933L4.37551 16.0251H4.37551L4.03502 16.6933ZM3.30666 15.965L3.97491 15.6245H3.97491L3.30666 15.965ZM16.6933 15.965L16.0251 15.6245L16.0251 15.6245L16.6933 15.965ZM15.965 16.6933L15.6245 16.0251L15.6245 16.0251L15.965 16.6933ZM16.6933 4.03502L16.0251 4.37551L16.0251 4.37551L16.6933 4.03502ZM15.965 3.30666L15.6245 3.97491L15.6245 3.97491L15.965 3.30666ZM3.30666 4.03502L2.6384 3.69452L2.6384 3.69452L3.30666 4.03502ZM4.03502 3.30666L3.69452 2.6384L3.69452 2.6384L4.03502 3.30666ZM10.75 3.125V2.375H9.25V3.125H10.75ZM3.125 7.375H2.375V8.875H3.125V7.375ZM9.25 16.875V17.625H10.75V16.875H9.25ZM16.875 12.625H17.625V11.125H16.875V12.625ZM16.125 5.79167V14.2083H17.625V5.79167H16.125ZM14.2083 16.125H5.79167V17.625H14.2083V16.125ZM3.875 14.2083V5.79167H2.375V14.2083H3.875ZM5.79167 3.875H14.2083V2.375H5.79167V3.875ZM5.79167 16.125C5.31258 16.125 4.99837 16.1244 4.75808 16.1048C4.52654 16.0859 4.43042 16.0531 4.37551 16.0251L3.69452 17.3616C3.99613 17.5153 4.31163 17.5733 4.63593 17.5998C4.95147 17.6256 5.33733 17.625 5.79167 17.625V16.125ZM2.375 14.2083C2.375 14.6627 2.37442 15.0485 2.4002 15.3641C2.42669 15.6884 2.48472 16.0039 2.6384 16.3055L3.97491 15.6245C3.94693 15.5696 3.91413 15.4735 3.89522 15.2419C3.87558 15.0016 3.875 14.6874 3.875 14.2083H2.375ZM4.37551 16.0251C4.20303 15.9372 4.0628 15.797 3.97491 15.6245L2.6384 16.3055C2.87009 16.7602 3.2398 17.1299 3.69452 17.3616L4.37551 16.0251ZM16.125 14.2083C16.125 14.6874 16.1244 15.0016 16.1048 15.2419C16.0859 15.4735 16.0531 15.5696 16.0251 15.6245L17.3616 16.3055C17.5153 16.0039 17.5733 15.6884 17.5998 15.3641C17.6256 15.0485 17.625 14.6627 17.625 14.2083H16.125ZM14.2083 17.625C14.6627 17.625 15.0485 17.6256 15.3641 17.5998C15.6884 17.5733 16.0039 17.5153 16.3055 17.3616L15.6245 16.0251C15.5696 16.0531 15.4735 16.0859 15.2419 16.1048C15.0016 16.1244 14.6874 16.125 14.2083 16.125V17.625ZM16.0251 15.6245C15.9372 15.797 15.797 15.9372 15.6245 16.0251L16.3055 17.3616C16.7602 17.1299 17.1299 16.7602 17.3616 16.3055L16.0251 15.6245ZM17.625 5.79167C17.625 5.33733 17.6256 4.95147 17.5998 4.63593C17.5733 4.31163 17.5153 3.99613 17.3616 3.69452L16.0251 4.37551C16.0531 4.43042 16.0859 4.52654 16.1048 4.75808C16.1244 4.99837 16.125 5.31258 16.125 5.79167H17.625ZM14.2083 3.875C14.6874 3.875 15.0016 3.87558 15.2419 3.89522C15.4735 3.91413 15.5696 3.94693 15.6245 3.97491L16.3055 2.6384C16.0039 2.48472 15.6884 2.42669 15.3641 2.4002C15.0485 2.37442 14.6627 2.375 14.2083 2.375V3.875ZM17.3616 3.69452C17.1299 3.2398 16.7602 2.87009 16.3055 2.6384L15.6245 3.97491C15.797 4.0628 15.9372 4.20303 16.0251 4.37551L17.3616 3.69452ZM3.875 5.79167C3.875 5.31258 3.87558 4.99837 3.89522 4.75808C3.91413 4.52654 3.94693 4.43042 3.97491 4.37551L2.6384 3.69452C2.48472 3.99613 2.42669 4.31163 2.4002 4.63593C2.37442 4.95147 2.375 5.33733 2.375 5.79167H3.875ZM5.79167 2.375C5.33733 2.375 4.95147 2.37442 4.63593 2.4002C4.31163 2.42669 3.99613 2.48472 3.69452 2.6384L4.37551 3.97491C4.43042 3.94693 4.52654 3.91413 4.75808 3.89522C4.99837 3.87558 5.31258 3.875 5.79167 3.875V2.375ZM3.97491 4.37551C4.06279 4.20303 4.20303 4.06279 4.37551 3.97491L3.69452 2.6384C3.2398 2.87009 2.87009 3.2398 2.6384 3.69452L3.97491 4.37551ZM9.25 3.125V8.125H10.75V3.125H9.25ZM10 7.375H3.125V8.875H10V7.375ZM9.25 8.125V11.875H10.75V8.125H9.25ZM9.25 11.875V16.875H10.75V11.875H9.25ZM10 12.625H16.875V11.125H10V12.625Z"
                  fill="currentColor"
                  fill-opacity="0.5"
                />
              </svg>
              <BaseText
                type="body"
                size="sm"
                class="text-text-muted truncate"
              >
                {{ board?.name?.substring(1) }}
              </BaseText>
            </router-link>
            <div
              class="ml-auto w-5 h-5 overflow-hidden flex flex-col relative cursor-pointer m-2 opacity-0 group-hover:opacity-100"
              @click="toggleStarredBoard(board)"
            >
              <div class="w-5 h-5 overflow-hidden flex flex-col relative">
                <div class="bg-white hover:bg-background-normal">
                  <svg
                    class="absolute w-5 h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="21"
                    viewBox="0 0 20 21"
                    fill="none"
                  >
                    <g id="star, favorite, award">
                      <path
                        id="Icon"
                        d="M9.42929 1.81641C9.65867 1.33897 10.3413 1.33897 10.5707 1.8164L12.5445 5.92471C12.6369 6.11693 12.8206 6.24963 13.0328 6.27741L17.5741 6.87197C18.1021 6.9411 18.3133 7.58925 17.9266 7.95385L14.6065 11.0841C14.4507 11.231 14.3803 11.4464 14.4195 11.6564L15.2533 16.1286C15.3504 16.6494 14.7979 17.0497 14.3301 16.7975L10.3012 14.6258C10.1133 14.5245 9.88671 14.5245 9.69879 14.6258L5.66994 16.7975C5.20214 17.0497 4.64963 16.6494 4.74671 16.1286L5.58051 11.6564C5.61966 11.4464 5.54926 11.231 5.39349 11.0841L2.07338 7.95385C1.68667 7.58925 1.89786 6.9411 2.4259 6.87197L6.96724 6.27741C7.17942 6.24963 7.36312 6.11693 7.45547 5.92471L9.42929 1.81641Z"
                        stroke="#5E6678"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </g>
                  </svg>
                </div>

                <div class="bg-white hover:bg-background-normal">
                  <svg
                    class="absolute w-5 h-5 hover:opacity-0 transition-all duration-200 ease-linear"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="21"
                    viewBox="0 0 20 21"
                    fill="none"
                  >
                    <g clip-path="url(#clip0_7870_63048)">
                      <path
                        d="M9.42929 1.98308C9.65867 1.50564 10.3413 1.50564 10.5707 1.98308L12.5445 6.09138C12.6369 6.2836 12.8206 6.4163 13.0328 6.44408L17.5741 7.03864C18.1021 7.10778 18.3133 7.75592 17.9266 8.12052L14.6065 11.2508C14.4507 11.3976 14.3803 11.6131 14.4195 11.8231L15.2533 16.2953C15.3504 16.8161 14.7979 17.2164 14.3301 16.9642L10.3012 14.7924C10.1133 14.6911 9.88671 14.6911 9.69879 14.7924L5.66994 16.9642C5.20214 17.2164 4.64963 16.8161 4.74671 16.2953L5.58051 11.8231C5.61966 11.6131 5.54926 11.3976 5.39349 11.2508L2.07338 8.12052C1.68667 7.75592 1.89786 7.10778 2.4259 7.03864L6.96724 6.44408C7.17942 6.4163 7.36312 6.2836 7.45547 6.09138L9.42929 1.98308Z"
                        fill="#FFEDD5"
                        stroke="#FBBF24"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_7870_63048">
                        <rect
                          width="20"
                          height="20"
                          fill="white"
                          transform="translate(0 0.166687)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else-if="!loading && !getStarredBoards?.length && getBoards?.length"
        class="flex w-1/2 h-86 flex-col justify-center md:justify-start items-center p-4 pt-0 ease-linear"
      >
        <img
          src="../../assets/images/default-stared.png"
          alt=""
          class="w-full md:w-4/5 -mb-8"
        >
        <BaseText
          type="label"
          size="md"
          class="text-text-muted"
        >
          Quick-Link Boards
        </BaseText>
        <BaseText
          type="body"
          size="sm"
          class="text-text-normal"
        >
          Star your most-used boards
        </BaseText>
        <a
          href="https://intercom.help/foreplay/en/articles/8202644-starred-boards"
          target="_blank"
          class="flex items-center justify-center cursor-pointer mt-3"
        >
          <BaseText
            type="label"
            size="sm"
            class="text-primary-blue-100"
          >
            Show Me How
          </BaseText>
          <svg
            class="transform rotate-90 ml-0.5 text-primary-blue-100"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.66675 11.6669L9.41083 8.92279C9.73626 8.59735 10.2639 8.59735 10.5893 8.92279L13.3334 11.6669"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </a>
      </div>
      <div
        v-else-if="loading"
        class="w-1/2 h-64 flex-col items-center justify-between overflow-y-scroll scrollbar-hide"
      >
        <BaseLoadingLogo />
      </div>
    </div>
    <CreateAdvertisementModal
      v-if="showAdvertisementModal"
      :starting-tab="'extension'"
      @close="showAdvertisementModal = false"
    />
    <ManageBoardModal
      v-if="showManageBoardModal"
      :board="{}"
      @close="showManageBoardModal = false"
    />
  </BaseCard>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import CreateAdvertisementModal from '../advertisements/AdvertisementCreateModal.vue'
import ManageBoardModal from '../boards/ManageBoardModal.vue'
import firebase from '../../api/config/FirebaseConfig'

export default {
  name: 'SwipeFileCard',
  components: {
    CreateAdvertisementModal,
    ManageBoardModal
  },
  data () {
    return {
      showAdvertisementModal: false,
      showManageBoardModal: false,
      loading: true,
      extensionStatuses: [
        {
          icon: 'facebook',
          text: 'Facebook Ad Library',
          status: 'Working',
          link: 'https://www.facebook.com/ads/library/'
        },
        {
          icon: 'instagram',
          text: 'Instagram Mobile',
          status: 'Working',
          link: 'https://www.foreplay.co/post/save-instagram-ads-on-mobile'
        },
        {
          icon: 'tik tok',
          text: 'TikTok Ad Library',
          status: 'Working',
          link: 'https://library.tiktok.com/ads'
        },
        {
          icon: 'tik tok',
          text: 'TikTok Top Ads',
          status: 'Working',
          link: 'https://ads.tiktok.com/business/creativecenter/inspiration/topads'
        },
        {
          icon: 'tik tok',
          text: 'TikTok Organic',
          status: 'Working',
          link: 'https://www.tiktok.com/'
        },
        {
          icon: 'tik tok',
          text: 'TikTok Spotlight',
          status: 'Working',
          link: 'https://ads.tiktok.com/business/creativecenter/tiktok-topads-spotlight/pc/en'
        },
        {
          icon: 'linkedin',
          text: 'LinkedIn Ads',
          status: 'Working',
          link: 'https://www.linkedin.com/ad-library/home'
        },
        {
          icon: 'google',
          text: 'Google Transparency',
          status: 'Coming Soon',
          link: 'https://transparencyreport.google.com/political-ads/region/US'
        }
      ]
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getStarredBoards']),
    ...mapGetters('BoardsModule', ['getBoards'])
  },
  async mounted () {
    await this.fetchBoards()
    setTimeout(() => {
      this.loading = false
    }, 1000)
  },
  methods: {
    ...mapActions('BoardsModule', ['fetchBoards']),
    ...mapMutations('AuthModule', ['SET_USER', 'SET_STARRED_BOARDS']),
    displayGreeting (name) {
      const currentTime = new Date()
      const currentHour = currentTime.getHours()
      let greeting = ''
      if (currentHour < 12) {
        greeting = 'Good Morning ' + name + '!'
      } else if (currentHour < 18) {
        greeting = 'Good Afternoon ' + name + '!'
      } else {
        greeting = 'Good Evening ' + name + '!'
      }
      return greeting
    },
    async toggleStarredBoard (board) {
      console.log(board, 'board')
      this.currentUser = this?.getUser

      if (!this?.currentUser?.starredBoards) {
        this.currentUser.starredBoards = []
      }

      const index = this?.currentUser.starredBoards?.indexOf(board.id)
      if (index === -1) {
        this.currentUser.starredBoards.push(board?.id)
      } else {
        this.currentUser.starredBoards.splice(index, 1)
      }

      this.SET_USER(this.currentUser)
      this.SET_STARRED_BOARDS(
        this.getBoards.filter((board) =>
          this.getUser.starredBoards?.includes(board.id)
        )
      )

      try {
        const db = firebase.firestore()
        delete this.currentUser.token

        await db.collection('auth-users').doc(this.currentUser.user_id).update({
          starredBoards: this.currentUser.starredBoards
        })
      } catch (err) {
        console.log(err)
      }
    }
  }
}
</script>
<style scoped></style>
